import {
  ApplicationRef,
  ComponentRef,
  createComponent,
  EnvironmentInjector,
  Injectable,
  OnDestroy,
} from '@angular/core';

import { HttpClient } from '@angular/common/http';

import { firstValueFrom } from 'rxjs';

import { SurveyBottomSheetComponent } from '../survey/survey-bottom-sheet.component';

import { SurveyPlayStorePromptComponent } from '../survey/survey-play-store-prompt.component';

import { isPromoterScore } from '../survey/survey-promoter';

import { ISurveyCampaign, IWebSurveySubmitResponse } from '../core.interface';

import { environment } from '../../environments/environment';
import { BrandingService } from './branding.service';

interface SurveyClosePayload {
  status?: string | number;
  score?: number;
  feedback?: string | null;
  comments?: string;
}

@Injectable({
  providedIn: 'root',
})
export class SurveyService implements OnDestroy {
  private readonly API_URL = `${environment.apiUrl}/web-survey`;

  private surveyTimer: ReturnType<typeof setTimeout> | undefined;

  private isSurveyShowing = false;

  private modalRef?: ComponentRef<SurveyBottomSheetComponent>;

  private playStoreModalRef?: ComponentRef<SurveyPlayStorePromptComponent>;

  constructor(
    private http: HttpClient,
    private appRef: ApplicationRef,
    private injector: EnvironmentInjector,
    private branding: BrandingService,
  ) {}

  ngOnDestroy(): void {
    if (this.surveyTimer) {
      clearTimeout(this.surveyTimer);
    }

    this.destroyModal();
    this.destroyPlayStoreModal();
  }

  async getSurvey(tenantId: string | number): Promise<ISurveyCampaign | null> {
    try {
      const response: { data?: ISurveyCampaign } = await firstValueFrom(
        this.http.get<{ data?: ISurveyCampaign }>(`${this.API_URL}/active/tenant/${tenantId}`),
      );

      if (!response?.data) {
        return null;
      }

      return {
        campaignId: response.data.campaignId ?? 0,

        campaignName: response.data.campaignName ?? '',

        campaignDetail: response.data.campaignDetail ?? '',

        campaignScore: response.data.campaignScore ?? [],
      };
    } catch (error) {
      console.error('[SURVEY ERROR]', error);

      return null;
    }
  }

  async openSurvey(tenantId: string | number): Promise<void> {
    if (!this.branding.isGGM()) {
      return;
    }

    if (this.isSurveyShowing) {
      return;
    }

    const today = new Date().toISOString().split('T')[0];

    const storageKey = `survey_shown_${tenantId}`;

    const lastShown = localStorage.getItem(storageKey);

    if (lastShown === today) {
      return;
    }

    const survey = await this.getSurvey(tenantId);

    if (!survey) {
      return;
    }

    localStorage.setItem(storageKey, today);

    this.isSurveyShowing = true;

    this.modalRef = createComponent(SurveyBottomSheetComponent, {
      environmentInjector: this.injector,
    });

    this.modalRef.instance.survey = survey;

    this.modalRef.instance.tenantId = tenantId;

    this.modalRef.instance.closeModal.subscribe(async (data: SurveyClosePayload | undefined) => {
      this.destroyModal();

      if (data === undefined || data === null) {
        return;
      }

      const body = {
        tenantId,

        status: this.getStatusId(data.status),

        score: data.score ?? 0,

        comments: data.feedback ?? data.comments ?? '',

        fromApp: 0,
      };

      const reviewData = await this.submitWebSurvey(survey.campaignId.toString(), body);

      if (
        reviewData &&
        this.isAnsweredSurvey(data) &&
        isPromoterScore(data.score, survey.campaignScore)
      ) {
        this.openExternalReviewPrompt(reviewData);
      }
    });

    this.appRef.attachView(this.modalRef.hostView);

    const domElement = (this.modalRef.hostView as unknown as { rootNodes: HTMLElement[] })
      .rootNodes[0] as HTMLElement;

    this.modalRef.changeDetectorRef.detectChanges();

    document.body.appendChild(domElement);
  }

  private destroyModal(): void {
    if (!this.modalRef) {
      return;
    }

    this.appRef.detachView(this.modalRef.hostView);

    this.modalRef.destroy();

    this.modalRef = undefined;

    this.isSurveyShowing = false;
  }

  private openExternalReviewPrompt(data: IWebSurveySubmitResponse): void {
    if (this.playStoreModalRef) {
      return;
    }

    if (data.requestMarketValoration) {
      return;
    }

    const hasSentence = !!data.googleReviewSentence?.trim();
    const hasEmoji = !!data.emoji?.trim();
    const hasGoogleCta = !!data.googleLink && !data.googleReviewAlreadySubmitted;

    if (!hasGoogleCta && !hasSentence && !hasEmoji) {
      return;
    }

    this.playStoreModalRef = createComponent(SurveyPlayStorePromptComponent, {
      environmentInjector: this.injector,
    });

    this.playStoreModalRef.setInput('googleReviewSentence', data.googleReviewSentence ?? '');
    this.playStoreModalRef.setInput('emoji', data.emoji ?? '');
    this.playStoreModalRef.setInput('googleLink', data.googleLink ?? null);

    this.playStoreModalRef.instance.closeModal.subscribe(() => {
      this.destroyPlayStoreModal();
    });

    this.playStoreModalRef.instance.reviewLinkClicked.subscribe(async () => {
      const link = data.googleLink;
      if (!link) {
        return;
      }

      await this.registerExternalReview(data.campaignId, data.surveyId);
      window.open(link, '_blank', 'noopener,noreferrer');
      this.destroyPlayStoreModal();
    });

    this.appRef.attachView(this.playStoreModalRef.hostView);

    const domElement = (this.playStoreModalRef.hostView as unknown as { rootNodes: HTMLElement[] })
      .rootNodes[0];

    this.playStoreModalRef.changeDetectorRef.detectChanges();
    document.body.appendChild(domElement);
  }

  private destroyPlayStoreModal(): void {
    if (!this.playStoreModalRef) {
      return;
    }

    this.appRef.detachView(this.playStoreModalRef.hostView);
    this.playStoreModalRef.destroy();
    this.playStoreModalRef = undefined;
  }

  private isAnsweredSurvey(data: { status?: string | number }): boolean {
    const status = data.status;

    if (status === 1 || status === '1') {
      return true;
    }

    return status?.toString().toLowerCase() === 'answered';
  }

  async submitWebSurvey(
    campaignId: string,
    body: Record<string, unknown>,
  ): Promise<IWebSurveySubmitResponse | null> {
    try {
      const response = await firstValueFrom(
        this.http.post<{ data?: IWebSurveySubmitResponse }>(`${this.API_URL}/${campaignId}`, body),
      );

      return response?.data ?? null;
    } catch (error) {
      console.error('[SURVEY SUBMIT ERROR]', error);

      return null;
    }
  }

  async registerExternalReview(campaignId: number, surveyId: number): Promise<boolean> {
    try {
      await firstValueFrom(this.http.patch(`${this.API_URL}/${campaignId}/${surveyId}`, null));

      return true;
    } catch (error) {
      console.error('[SURVEY PATCH ERROR]', error);
      return false;
    }
  }

  startSurveyTimer(tenantId: string | number): void {
    if (!this.branding.isGGM()) {
      return;
    }

    if (!this.shouldOfferSurveyOnThisVisit()) {
      return;
    }

    if (this.surveyTimer) {
      clearTimeout(this.surveyTimer);
    }

    this.surveyTimer = setTimeout(async () => {
      await this.openSurvey(tenantId);
    }, 60000);
  }

  getStatusId(rawStatus: string | number | undefined): number {
    const status = (rawStatus ?? 'OMITTED').toString().toUpperCase();

    const mapping: Record<string, number> = {
      OMITTED: 0,

      ANSWERED: 1,

      REJECTED: 2,
    };

    return mapping[status] ?? 0;
  }

  private getHomeVisitStorageKey(): string {
    try {
      const raw = sessionStorage.getItem('ggm-user');
      if (raw) {
        const parsed = JSON.parse(raw) as { userId?: number };
        if (parsed.userId != null) {
          return `ggm_ov_home_visit_count_${parsed.userId}`;
        }
      }
    } catch {
      // sessionStorage no disponible o JSON inválido
    }
    return 'ggm_ov_home_visit_count';
  }

  private getHomeVisitCount(): number {
    try {
      const raw = localStorage.getItem(this.getHomeVisitStorageKey());
      if (!raw) {
        return 0;
      }
      const parsed = Number.parseInt(raw, 10);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
    } catch {
      return 0;
    }
  }

  /** Incrementa visitas a home autenticado; devuelve el número de visita actual. */
  private recordHomeVisit(): number {
    const next = this.getHomeVisitCount() + 1;
    try {
      localStorage.setItem(this.getHomeVisitStorageKey(), String(next));
    } catch {
      // Storage lleno o bloqueado
    }
    return next;
  }

  private shouldOfferSurveyOnThisVisit(): boolean {
    return this.recordHomeVisit() >= 2;
  }
}
